import { FC, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ConfigEditorShell from "../../components/ConfigEditorShell/ConfigEditorShell";
import {
	useConfigEditor,
	type ExitRoute,
} from "../../components/ConfigEditorShell/useConfigEditor";
import { CreatePayer, PayerResponse } from "@/domain/payer.interface";
import MemberIdValidation from "./MemberIdValidation";
import { categoryLabel, selectableCategories } from "./payerCategories";
import { usePayerCategories } from "../../hooks/useCategories";
import { isPatternUsable } from "./memberIdPattern";
import PatternChangeWarning from "./PatternChangeWarning";
import { usePatternImpact } from "../../hooks/userPayers";

/**
 * SIMS-8614 — the full-page payer editor.
 *
 * Replaces `PayerDialog` for editing and creating. Rendered in place of the list rather than at
 * its own route (developer decision 2026-08-28): all four ACs hold without touching shared
 * routing, and no AC asks to deep-link an editor.
 *
 * Fields follow `Payers Pattern v2.dc.html:387-434` — a three-up top row
 * (`1.6fr 200px 200px`: name, Member IDs mode, status), then description full width, inside a
 * titled section card. The member-ID pattern field and its validation panel arrive with
 * SIMS-8607; the mode-change notice with SIMS-8653; categories with SIMS-8634.
 *
 * Deliberately NOT react-hook-form, unlike the old `PayerForm`: dirty state must be the editor
 * shell's single source of truth (AC1/AC2), and RHF keeping its own `formState.isDirty` in
 * parallel is exactly how the pill and the discard prompt would come to disagree.
 */

export interface PayerEditorProps {
	/** The payer being edited, or null to create one. */
	payer: PayerResponse | null;
	onSubmit: (data: CreatePayer) => Promise<void>;
	/** Leave the editor — the shell has already cleared any unsaved-changes prompt. */
	onExit: () => void;
	/**
	 * Delete the payer being edited (design L405-410). Omitted when creating: the design gates the
	 * button on `!isNew`, and there is nothing to delete.
	 *
	 * Opens the list's own confirmation, so the dependency counts, the refusal and the server-side
	 * 409 all still apply — this is a second way IN, not a second delete path.
	 */
	onDelete?: () => void;
}

type PayerDraft = {
	name: string;
	description: string;
	regularExpression: string;
	autogenerated: string;
	status: string;
	/** SIMS-8634 — applied CategoryItem ids (AC1: many; AC2: optional). */
	categories: string[];
};

const emptyDraft: PayerDraft = {
	name: "",
	description: "",
	regularExpression: "",
	autogenerated: "N",
	status: "ACTIVE",
	categories: [],
};

const PayerEditor: FC<PayerEditorProps> = ({
	payer,
	onSubmit,
	onExit,
	onDelete,
}) => {
	const initial: PayerDraft = payer
		? {
				name: payer.name,
				description: payer.description,
				regularExpression: payer.regularExpression,
				autogenerated: payer.autogenerated,
				status: payer.status,
				categories: payer.categories ?? [],
			}
		: emptyDraft;

	const editor = useConfigEditor<PayerDraft>({
		initial,
		onSave: async (draft) => {
			await onSubmit(draft as CreatePayer);
		},
	});

	// One prompt, four routes (hand-off §2 rule 3). The shell decides whether to ask; the route
	// is passed through only so the destination differs, never the question.
	const leave = (route: ExitRoute) => editor.requestExit(onExit, route);

	// SIMS-8607 AC4 — an unusable pattern refuses the save. Touched-on-blur so an untouched
	// field is not marked before the user has reached it.
	const [patternTouched, setPatternTouched] = useState(false);
	const patternOk = isPatternUsable(editor.draft.regularExpression);
	const nameMissing = editor.draft.name.trim() === "";

	// AC5 — the duplicate-name error belongs ON THE NAME FIELD (plan D-2: mirror the pattern
	// field's error row). The uniqueness CHECK is backend (plan D-1, ConflictException,
	// per-account) and does not exist yet, so this surfaces a 409 when one arrives rather than
	// pre-checking against the fetched list — a pre-check races between two administrators and
	// would be a false guarantee.
	const duplicateName = /already (exists|in use)/i.test(editor.saveError ?? "");

	// SIMS-8653 AC1 — a mode change applies to new registrations only, and the design has no
	// element saying so (SR-5): validationHelp changes wording by mode but never mentions the
	// members already registered. Shown when the draft mode differs from the SAVED mode on an
	// EXISTING payer: on a new payer, or once the mode is put back, there are no existing
	// registrations for the change to spare, so the notice would be noise.
	//
	// An inline notice rather than a modal, unlike the pattern-change warning: that one can
	// orphan existing member IDs, whereas this is reversible and touches only future
	// registrations. Blocking the form would be heavier than the risk.
	const modeChanged =
		payer !== null &&
		editor.draft.autogenerated !== editor.savedSnapshot.autogenerated;

	/**
	 * SIMS-8622 AC1/AC4 — a CHANGED pattern on an EXISTING payer asks first.
	 *
	 * AC4 is why both conditions matter: editing a description must not raise the warning, and a
	 * brand-new payer has no existing member IDs to orphan. `savedSnapshot` is the comparison, not
	 * the initial draft, so re-editing after a successful save compares against what was saved.
	 */
	const patternChanged =
		payer !== null &&
		editor.draft.regularExpression !== editor.savedSnapshot.regularExpression;

	const impact = usePatternImpact(
		payer?._id ?? null,
		editor.draft.regularExpression,
	);
	const [warningOpen, setWarningOpen] = useState(false);

	const handleSave = () => {
		setPatternTouched(true);
		if (!patternOk || nameMissing) {
			return;
		}
		if (patternChanged) {
			// Fetched here rather than as the administrator types: each call runs a regex over the
			// beneficiary collection, so it happens once, at the moment the decision is made.
			void impact.refetch();
			setWarningOpen(true);
			return;
		}
		void editor.save();
	};

	/** AC2 — continuing applies the new pattern; no existing member ID is altered. */
	const handleWarningContinue = () => {
		setWarningOpen(false);
		void editor.save();
	};

	/** AC3 — cancelling leaves the original pattern in place, and the editor stays open. */
	const handleWarningCancel = () => {
		setWarningOpen(false);
	};

	// SIMS-8634 — AC4 offers only active categories, AC5 keeps one the payer already carries so
	// the picker can render its own value (dropping it would lose it on save, as in SIMS-8876).
	const { data: categoryDoc } = usePayerCategories();
	const offered = selectableCategories(
		categoryDoc?.categories ?? [],
		editor.draft.categories,
	);

	const toggleCategory = (id: string): void => {
		const applied = editor.draft.categories;
		editor.setField(
			"categories",
			applied.includes(id) ? applied.filter((c) => c !== id) : [...applied, id],
		);
	};

	const isNew = payer === null;

	return (
		<ConfigEditorShell
			discardBody="This payer has changes that have not been saved. Discarding closes the editor and leaves the payer as it was."
			breadcrumbs={[
				{ label: "Program setup" },
				{ label: "Payers", onClick: () => leave("breadcrumb") },
			]}
			title={isNew ? "New payer" : payer.name}
			isDirty={editor.isDirty}
			isSaving={editor.isSaving}
			canSave={editor.canSave}
			saveError={editor.saveError}
			failedTitle="Payer wasn't saved"
			discardPromptOpen={editor.discardPromptOpen}
			onSave={handleSave}
			// Only for an existing payer (design `canDelete: !!editPayer && !s.isNew`).
			onDelete={payer && onDelete ? onDelete : undefined}
			onCancel={() => leave("cancel")}
			onBreadcrumb={() => leave("breadcrumb")}
			onKeepEditing={editor.keepEditing}
			requestExit={editor.requestExit}
			onDiscard={editor.discard}
		>
			<Box
				sx={{
					backgroundColor: "background.paper",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: "0.75rem",
					padding: "1.25rem 1.5rem 1.5rem",
					display: "flex",
					flexDirection: "column",
					gap: 2.5,
				}}
			>
				<Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
					Payer details
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1.6fr 12.5rem 12.5rem" },
						gap: 2.5,
						alignItems: "flex-start",
					}}
				>
					<TextField
						label="Payer name"
						required
						value={editor.draft.name}
						onChange={(e) => editor.setField("name", e.target.value)}
						placeholder="e.g. Medicaid MT"
						error={duplicateName || (patternTouched && nameMissing)}
						helperText={
							duplicateName
								? "A payer with this name already exists. Choose a different name."
								: patternTouched && nameMissing
									? "A payer name is required."
									: undefined
						}
						fullWidth
					/>

					<FormControl fullWidth required>
						<InputLabel id="payer-mode-label">Member IDs</InputLabel>
						<Select
							labelId="payer-mode-label"
							label="Member IDs"
							value={editor.draft.autogenerated}
							onChange={(e) => editor.setField("autogenerated", e.target.value)}
						>
							{/* "Y"/"N" strings, not booleans — the model stores an enum and changing it
							    needs a migration (see the Epic plan). */}
							<MenuItem value="N">Entered manually</MenuItem>
							<MenuItem value="Y">Autogenerated</MenuItem>
						</Select>
					</FormControl>

					<FormControl fullWidth required>
						<InputLabel id="payer-status-label">Status</InputLabel>
						<Select
							labelId="payer-status-label"
							label="Status"
							value={editor.draft.status}
							onChange={(e) => editor.setField("status", e.target.value)}
						>
							<MenuItem value="ACTIVE">Active</MenuItem>
							<MenuItem value="INACTIVE">Inactive</MenuItem>
						</Select>
					</FormControl>
				</Box>

				{/* Category chips (design L421-428): toggle pills, 32px, 999px radius, 13px/500.
				    A retired one is dashed at 60% opacity with an "(inactive)" suffix — NFR-5
				    forbids conveying state by appearance alone. */}
				{offered.length > 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Box
							component="span"
							sx={{
								fontSize: "0.75rem",
								fontWeight: 500,
								color: "text.secondary",
							}}
						>
							Payer category
						</Box>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							{offered.map((cat) => {
								const on = editor.draft.categories.includes(cat._id);
								const retired = cat.status === "INACTIVE";
								return (
									<Box
										key={cat._id}
										component="button"
										type="button"
										aria-pressed={on}
										onClick={() => toggleCategory(cat._id)}
										sx={{
											height: "2rem",
											padding: "0 0.875rem",
											borderRadius: "999px",
											fontSize: "0.8125rem",
											fontWeight: 500,
											cursor: "pointer",
											border: "1px",
											borderStyle: retired ? "dashed" : "solid",
											borderColor: on ? "primary.main" : "divider",
											backgroundColor: on
												? (t) => t.palette.surfaceTint.main
												: "background.paper",
											color: on ? "primary.dark" : "text.secondary",
											opacity: retired ? 0.6 : 1,
										}}
									>
										{categoryLabel(cat)}
									</Box>
								);
							})}
						</Box>
						<Box
							component="span"
							sx={{ fontSize: "0.75rem", color: "text.secondary" }}
						>
							Categories separate real payers from test records. Inactive
							categories stay on payers that already carry them.
						</Box>
					</Box>
				)}

				{/* NOT required: SIMS-8607 AC1 names "name, member-ID mode, status and pattern",
				    and TestRail C17569 tests exactly those three plus the pattern. The design marks
				    it without an asterisk in both payer screens. The BACKEND disagrees — it has
				    @IsNotEmpty() on description and `required: true` on the model, both predating
				    this epic — so an empty description is refused by the API with "description
				    should not be empty". Tracked as a backend/spec conflict rather than papered
				    over here by inventing a requirement the ACs do not state. */}
				<TextField
					label="Description"
					value={editor.draft.description}
					onChange={(e) => editor.setField("description", e.target.value)}
					multiline
					minRows={2}
					fullWidth
				/>
			</Box>

			{modeChanged && (
				<Alert severity="info" data-testid="mode-change-notice">
					{editor.draft.autogenerated === "Y"
						? "New registrations will have their member IDs generated from the pattern. This applies to new registrations only — members already registered keep the IDs they have."
						: "New registrations will have their member IDs entered by hand. This applies to new registrations only — members already registered keep the IDs they have."}
				</Alert>
			)}

			<PatternChangeWarning
				open={warningOpen}
				mismatch={impact.data?.mismatch ?? 0}
				total={impact.data?.total ?? 0}
				unavailable={impact.isError || (warningOpen && !impact.data)}
				onCancel={handleWarningCancel}
				onContinue={handleWarningContinue}
			/>

			<MemberIdValidation
				pattern={editor.draft.regularExpression}
				onPatternChange={(next) => {
					setPatternTouched(true);
					editor.setField("regularExpression", next);
				}}
				autogenerated={editor.draft.autogenerated}
				patternTouched={patternTouched}
			/>
		</ConfigEditorShell>
	);
};

export default PayerEditor;
